/**
 * Supplier-specific PO forms for board POs.  Some mills want the order on
 * their own form (Century: "Order Booking Form - Board"); a board PO to such
 * a supplier can print on it as well as on the company PO form.
 *
 * Adding a supplier: write a build(po) that returns the form's HTML (inline
 * styles only, as buildBoardPoHtml does), and add an entry to
 * VENDOR_PO_FORMATS with a matches() on the vendor's name.
 */
import { qtyText, sheetKg, type BoardPoDoc, type BoardPoDocLine } from './boardPo';
import { CENTURY_CENTRED, CENTURY_LABELS, CENTURY_LOGO, CENTURY_RECTS, CENTURY_ROWS } from './centuryForm';

/** A column the supplier's form has that our PO line does not (Grain ...). */
export interface VendorLineField {
  key: string;
  label: string;
  /** A fixed choice; the first is the default. Free text when absent. */
  options?: string[];
  placeholder?: string;
  /** Width in the editor, in the 12-column grid. */
  span: number;
}

export interface VendorPoFormat {
  key: string;
  /** Extra per-line columns, set on the PO form when this supplier is picked. */
  lineFields: VendorLineField[];
  /** Shown on the buttons: "Century form". */
  label: string;
  matches: (vendorName: string) => boolean;
  orientation: 'portrait' | 'landscape';
  build: (po: BoardPoDoc) => string;
}


function esc(s: string | number | null | undefined): string {
  if (s == null || s === '') return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** 03.10.26 */
function dotDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}.${y.slice(2)}` : iso;
}

const cm = (inch: number | null | undefined) => (inch ? (Math.round(inch * 2.54 * 10) / 10).toString() : '');

/**
 * A line's quantity in metric tonnes: sheets by the board's weight per sheet,
 * kg / 1000, tonnes as they are.  Null when it cannot be worked out (packets,
 * or a board without its size or GSM).
 */
export function lineMt(l: BoardPoDocLine): number | null {
  const u = (l.uom || '').toUpperCase();
  if (u === 'TON') return l.qty;
  if (u === 'KG') return l.qty / 1000;
  if (u === 'SHEETS') {
    const kg = sheetKg(l.board ?? null);
    return kg > 0 ? (l.qty * kg) / 1000 : null;
  }
  return null;
}

// ------------------------------------------------------------------ Century

/**
 * Century Pulp & Paper - ORDER BOOKING FORM - BOARD (PM-6), DOC NO. MKT-F-02.
 * Drawn from Century's own PDF (lib/centuryForm.ts): the rules, boxes, labels
 * and logo exactly as on their form, with the PO written into its cells.
 */
function buildCenturyHtml(po: BoardPoDoc): string {
  const FONT = "Calibri, Carlito, 'Segoe UI', Arial, sans-serif";
  const t = (x: number, y: number, size: number, s: string, anchor: 'start' | 'middle' | 'end' = 'start') =>
    s ? `<text x="${x}" y="${y}" font-size="${size}" text-anchor="${anchor}">${esc(s)}</text>` : '';
  const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

  const rects = CENTURY_RECTS.split(';').map((r) => {
    const [x, y, w, h, white] = r.split(',');
    return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${white === '1' ? '#fff' : '#000'}"/>`;
  }).join('');
  const labels = CENTURY_LABELS.map(([x, y, s, txt]) => t(x, y, s, txt)).join('')
    + CENTURY_CENTRED.map(([x, y, s, txt]) => t(x, y, s, txt, 'middle')).join('');

  // The 13 item lines; the last one also carries the total, as on the form.
  const MAXLINES = CENTURY_ROWS.length - 1;
  const lines = po.lines.slice(0, MAXLINES);
  const mts = po.lines.map(lineMt);
  const totalMt = mts.reduce<number>((s, v) => s + (v ?? 0), 0);
  const rows = CENTURY_ROWS.map((y, i) => {
    const l = lines[i];
    // Only the PO's own lines - the blank form's pre-printed numbers and
    // SHEET / LONG / X on every row are left out.
    if (!l) return '';
    const f = l.formFields ?? {};
    const sheetLongX = t(i + 1 >= 10 ? 61.6 : 63.1, y, 6, String(i + 1))
      + t(280.2, y, 6, f.reelSheet || 'SHEET') + t(325.9, y, 6, f.grain || 'LONG') + t(403.8, y, 6, 'X');
    const b = l.board;
    const mt = mts[i];
    return sheetLongX
      + t(583, y, 6, clip(f.discount || '', 8), 'middle')
      + t(80, y, 6, clip(b?.paperType || l.description, 30))
      + t(227.1, y, 6, b?.gsm ? String(b.gsm) : '', 'middle')
      + t(375.5, y, 6, cm(b?.lengthIn), 'middle')
      + t(432.6, y, 6, cm(b?.widthIn), 'middle')
      + t(564, y, 6, mt != null ? mt.toFixed(3) : `${qtyText(l.qty)} ${l.uom}`, 'end')
      + t(602, y, 6, clip(f.remarks || (b?.paperType && l.description !== b.paperType ? l.description : ''), 40));
  }).join('');
  const extra = po.lines.length > MAXLINES ? t(602, CENTURY_ROWS[MAXLINES]!, 6, `+ ${po.lines.length - MAXLINES} more line(s) - see the PO`) : '';
  const remarks = po.remarks?.trim() ? t(602, CENTURY_ROWS[Math.min(lines.length, MAXLINES - 1)]!, 6, clip(`Remarks: ${po.remarks.trim()}`, 40)) : '';

  const poSize = Math.min(7.6, 50 / Math.max(1, po.poNumber.length * 0.52));
  const values = t(614.1, 80.5, 7.6, dotDate(po.poDate)) + t(627.1, 103.3, poSize, po.poNumber, 'middle')
    + rows + extra + remarks
    + t(564, CENTURY_ROWS[MAXLINES]!, 5.6, totalMt.toFixed(3), 'end');

  const L = CENTURY_LOGO;
  // Cropped to the drawn form (it sits in the top of Century's page), so it
  // fills the A4 sheet across.
  return `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="44 48 702 344" style="display:block;width:281mm;height:137.7mm;"
       font-family="${FONT}" font-weight="bold" fill="#000">
    <rect x="44" y="48" width="702" height="344" fill="#fff"/>
    ${rects}
    <image x="${L.x}" y="${L.y}" width="${L.w}" height="${L.h}" href="${L.src}" preserveAspectRatio="xMidYMid meet"/>
    ${labels}
    ${values}
  </svg>`;
}

export const VENDOR_PO_FORMATS: VendorPoFormat[] = [
  {
    key: 'century',
    label: 'Century form',
    lineFields: [
      { key: 'reelSheet', label: 'Reel / Sheet', options: ['SHEET', 'REEL'], span: 2 },
      { key: 'grain', label: 'Grain', options: ['LONG', 'SHORT'], span: 2 },
      { key: 'discount', label: 'Discount', placeholder: 'e.g. 2%', span: 2 },
      { key: 'remarks', label: 'Remarks (reel dia / core / FSC / packing)', placeholder: 'e.g. FSC, 100 sheets/packet', span: 6 },
    ],
    matches: (v) => /century/i.test(v),
    orientation: 'landscape',
    build: buildCenturyHtml,
  },
];

/** The supplier's own form for this PO, if it has one (board POs only). */
export function vendorFormatFor(po: BoardPoDoc): VendorPoFormat | undefined {
  return vendorFormatForName(po.vendorName, po.kind);
}

/** The same, from the supplier's name - for the PO form while it is filled. */
export function vendorFormatForName(vendorName: string | null | undefined, kind?: string): VendorPoFormat | undefined {
  if (kind && kind !== 'BOARD') return undefined;
  return VENDOR_PO_FORMATS.find((f) => f.matches(vendorName || ''));
}
